import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Flex, Button, Spin } from 'antd';
import { useTranslation } from 'react-i18next';

import { fetchSearch } from 'store/slices/productSlice';
import useSmartNavigate from 'hooks/useSmartNavigate';
import { formatWeightWithUnit } from 'helpers/formatter';
import { getStock } from 'helpers/product';
import ProductPriceRow from 'components/product/ProductPriceRow';
import { loadingStatus } from 'helpers/fetcher';
import CartQtyButtonGroup from 'components/cart/CartQtyButtonGroup';
import Icon from 'components/Icon';

const SearchWithSuggest = ({
	listHeight = 700,
	isDesktop = true,
	onClose = () => {},
	hideInput = false,
	externalSearchValue = '',
}) => {
	const { t } = useTranslation();
	const inputRef = useRef(null);
	const wrapperRef = useRef(null);
	const dropdownRef = useRef(null);
	const dispatch = useDispatch();
	const { data, status } = useSelector(state => state.product.search);
	const { navigate } = useSmartNavigate();
	const [searchValue, setSearchValue] = useState('');
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	// Используем внешнее значение, если оно передано
	const currentSearchValue = externalSearchValue || searchValue;

	const handleSuggest = s => {
		const value = s || currentSearchValue;
		if (value && value.trim()) {
			dispatch(fetchSearch(value));
			setIsDropdownOpen(true);
		} else {
			setIsDropdownOpen(false);
		}
	};

	// Отслеживаем изменения внешнего значения поиска
	useEffect(() => {
		if (externalSearchValue) {
			if (externalSearchValue.trim()) {
				dispatch(fetchSearch(externalSearchValue));
				setIsDropdownOpen(true);
			} else {
				setIsDropdownOpen(false);
			}
		}
	}, [externalSearchValue, dispatch]);

	const handleGoSearchPage = s => {
		if (s && s.trim()) {
			navigate(`/catalog?search=${encodeURIComponent(s.trim())}`);
			setIsDropdownOpen(false);
			setSearchValue('');
		}
	};

	const handleGoProductPage = product => {
		onClose();
		setIsDropdownOpen(false);
		setSearchValue('');
		navigate(`/product/${product.product_id}/${product.slug}`);
	};

	const handleInputChange = e => {
		const value = e.target.value;
		setSearchValue(value);
		handleSuggest(value);
	};

	const handleInputFocus = () => {
		if (data.products && data.products.length > 0 && searchValue) {
			setIsDropdownOpen(true);
		}
	};

	const handleInputBlur = e => {
		// Задержка, чтобы клик по элементу успел сработать
		setTimeout(() => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(document.activeElement)
			) {
				setIsDropdownOpen(false);
			}
		}, 200);
	};

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus({ cursor: 'end' });
		}
	}, []);

	useEffect(() => {
		const handleClickOutside = event => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target)
			) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const mobileSuggestionsOpen =
		!isDesktop && isDropdownOpen && currentSearchValue.trim();

	// Мобильный поиск: не даём странице за списком прокручиваться, пока открыты подсказки
	useEffect(() => {
		if (!mobileSuggestionsOpen) return undefined;

		const root = document.documentElement;
		const body = document.body;

		const prevRootOverflow = root.style.overflow;
		const prevBodyOverflow = body.style.overflow;
		const prevRootOs = root.style.overscrollBehavior;
		const prevBodyOs = body.style.overscrollBehavior;

		root.style.overflow = 'hidden';
		body.style.overflow = 'hidden';
		root.style.overscrollBehavior = 'none';
		body.style.overscrollBehavior = 'none';

		return () => {
			root.style.overflow = prevRootOverflow;
			body.style.overflow = prevBodyOverflow;
			root.style.overscrollBehavior = prevRootOs;
			body.style.overscrollBehavior = prevBodyOs;
		};
	}, [mobileSuggestionsOpen]);

	useEffect(() => {
		if (isDropdownOpen && wrapperRef.current && dropdownRef.current) {
			const updatePosition = () => {
				const wrapper = wrapperRef.current;
				const dropdown = dropdownRef.current;
				if (wrapper && dropdown) {
					const rect = wrapper.getBoundingClientRect();
					const viewportHeight = window.innerHeight;
					const viewportWidth = window.innerWidth;
					const dropdownHeight = 600; // max-height из CSS

					if (isDesktop) {
						// Позиционирование для десктопа
						// Добавляем небольшой отступ, чтобы меню не перекрывало строку поиска
						let top = rect.bottom + 4;
						let left = rect.left;
						const minWidth = 500; // Минимальная ширина выпадающего меню
						let width = Math.max(rect.width, minWidth);

						// Если выпадающее меню выходит за нижний край экрана
						if (top + dropdownHeight > viewportHeight) {
							// Ограничиваем высоту, чтобы не выходило за экран
							const maxHeight = viewportHeight - top - 10;
							dropdown.style.maxHeight = `${maxHeight}px`;
						} else {
							dropdown.style.maxHeight = `${dropdownHeight}px`;
						}

						// Проверка правого края
						if (left + width > viewportWidth) {
							left = viewportWidth - width - 10;
						}

						// Проверка левого края
						if (left < 10) {
							left = 10;
						}

						dropdown.style.top = `${top}px`;
						dropdown.style.left = `${left}px`;
						dropdown.style.width = `${width}px`;
					} else {
						// Позиционирование для мобильных
						let top = rect.bottom + 4;
						const maxHeight = Math.min(
							viewportHeight - top - 20,
							dropdownHeight
						);

						dropdown.style.top = `${top}px`;
						dropdown.style.maxHeight = `${maxHeight}px`;
						// left и width устанавливаются через CSS класс search-dropdown-mobile
					}
				}
			};
			updatePosition();
			window.addEventListener('scroll', updatePosition, true);
			window.addEventListener('resize', updatePosition);
			return () => {
				window.removeEventListener('scroll', updatePosition, true);
				window.removeEventListener('resize', updatePosition);
			};
		}
	}, [isDropdownOpen, data.products, isDesktop]);

	// Если hideInput=true, не показываем инпут, только выпадающий список
	// Позиционирование для скрытого инпута
	useEffect(() => {
		if (hideInput && isDropdownOpen && dropdownRef.current) {
			const updatePosition = () => {
				const dropdown = dropdownRef.current;
				if (dropdown) {
					// Ищем родительский контейнер мобильного поиска
					const mobileSearchWrapper = dropdown.closest(
						'.mobile-search-wrapper'
					);
					if (mobileSearchWrapper) {
						const rect = mobileSearchWrapper.getBoundingClientRect();
						const viewportHeight = window.innerHeight;
						const dropdownHeight = 600;

						let top = rect.bottom + 4;
						const maxHeight = Math.min(
							viewportHeight - top - 20,
							dropdownHeight
						);

						dropdown.style.top = `${top}px`;
						dropdown.style.maxHeight = `${maxHeight}px`;
					}
				}
			};
			updatePosition();
			window.addEventListener('scroll', updatePosition, true);
			window.addEventListener('resize', updatePosition);
			return () => {
				window.removeEventListener('scroll', updatePosition, true);
				window.removeEventListener('resize', updatePosition);
			};
		}
	}, [hideInput, isDropdownOpen, data.products]);

	if (hideInput) {
		return (
			<>
				{isDropdownOpen && currentSearchValue.trim() && (
					<div
						className={`search-dropdown ${
							isDesktop ? 'search-dropdown-desktop' : 'search-dropdown-mobile'
						}`}
						ref={dropdownRef}
					>
						<div className='search-dropdown-content'>
							{status === loadingStatus.LOADING ? (
								<div style={{ padding: '40px', textAlign: 'center' }}>
									<Spin size="large" />
								</div>
							) : data.products && data.products.length > 0 ? (
								data.products.map((product, index) => (
									<div
										key={product.product_id || index}
										className='search-dropdown-item'
										onClick={e => {
											if (
												!e.target.closest('.popular-product-button') &&
												!e.target.closest('.add-to-cart-quantity-group')
											) {
												handleGoProductPage(product);
											}
										}}
									>
										<SuggestItem item={product} isDesktop={isDesktop} />
									</div>
								))
							) : (
							<div className='search-dropdown-empty'>{t('common.nothingFound')}</div>
						)}
					</div>
				</div>
			)}
		</>
	);
	}

	return (
		<div className='search-with-suggest-wrapper' ref={wrapperRef}>
			{isDesktop ? (
				<Flex gap={0} style={{ width: '100%', alignItems: 'center' }}>
					<Input
						size='large'
						placeholder={t('common.searchPlaceholder')}
						prefix={<Icon name='icon-search' style={{ width: 24, height: 24 }} />}
						className='search-input'
						value={searchValue}
						onChange={handleInputChange}
						onFocus={handleInputFocus}
						onBlur={handleInputBlur}
						onPressEnter={e => {
							e.stopPropagation();
							handleGoSearchPage(e.target.value);
						}}
						ref={inputRef}
					/>
					<Button
						type='primary'
						className='search-button'
						onClick={() => {
							if (inputRef.current) {
								handleGoSearchPage(inputRef.current.input.value);
							}
						}}
					>
						{t('common.find')}
					</Button>
				</Flex>
			) : (
				<Input
					size='large'
					placeholder={t('common.searchPlaceholder')}
					value={searchValue}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					onBlur={handleInputBlur}
					onPressEnter={e => e.stopPropagation()}
					ref={inputRef}
				/>
			)}
			{isDropdownOpen && currentSearchValue.trim() && (
				<div
					className={`search-dropdown ${
						isDesktop ? 'search-dropdown-desktop' : 'search-dropdown-mobile'
					}`}
					ref={dropdownRef}
				>
					<div className='search-dropdown-content'>
						{status === loadingStatus.LOADING ? (
							<div style={{ padding: '40px', textAlign: 'center' }}>
								<Spin size="large" />
							</div>
						) : data.products && data.products.length > 0 ? (
							data.products.map((product, index) => (
								<div
									key={product.product_id || index}
									className='search-dropdown-item'
									onClick={e => {
										// Переход на страницу товара только если клик не по кнопкам
										if (
											!e.target.closest('.popular-product-button') &&
											!e.target.closest('.add-to-cart-quantity-group')
										) {
											handleGoProductPage(product);
										}
									}}
								>
									<SuggestItem item={product} isDesktop={isDesktop} />
								</div>
							))
						) : (
						<div className='search-dropdown-empty'>{t('common.nothingFound')}</div>
					)}
				</div>
			</div>
		)}
	</div>
	);
};

export default SearchWithSuggest;

const SuggestItem = ({ item, isDesktop }) => {
	const { t } = useTranslation();
	const stock = getStock(item);
	let stockText;
	if (stock.stock === 0) {
		stockText = t('stock.unavailable');
	} else if (stock.stock === 999) {
		stockText = t('stock.inStockMany');
	} else {
		stockText = t('stock.inStockCount', { count: stock.stock });
	}

	return (
		<Flex
			justify='space-between'
			align='center'
			style={{ width: '100%', gap: '16px' }}
		>
			<Flex align='center' style={{ flex: 1, minWidth: 0, gap: '16px' }}>
				<img
					alt={item.name}
					src={item.thumb}
					style={{
						width: 60,
						height: 60,
						objectFit: 'cover',
						borderRadius: '8px',
						flexShrink: 0,
					}}
				/>
				<div style={{ whiteSpace: 'normal', flex: 1, minWidth: 0 }}>
					<div
						style={{
							fontSize: '16px',
							fontWeight: 400,
							color: '#333',
							marginBottom: '4px',
							lineHeight: '1.4',
						}}
					>
						{`${item.name}, ${formatWeightWithUnit(item.weight)}`}
					</div>
					{isDesktop ? (
						<ProductPriceRow
							item={item}
							priceClassName='popular-product-price'
							priceStyle={{
								fontSize: '18px',
								fontWeight: 600,
								color: 'var(--rn-red-color)',
							}}
						/>
					) : (
						<>
							<div style={{ marginBottom: '8px' }}>
								<ProductPriceRow
									item={item}
									priceClassName='popular-product-price'
									priceStyle={{
										fontSize: '18px',
										fontWeight: 600,
										color: 'var(--rn-red-color)',
									}}
								/>
							</div>
							<div
								className={`search-dropdown-stock ${
									stock.stock === 0 ? 'search-dropdown-stock-unavailable' : ''
								}`}
								style={{ marginBottom: '8px' }}
							>
								{stockText}
							</div>
							<CartQtyButtonGroup item={item} size='small' />
						</>
					)}
				</div>
			</Flex>
			{isDesktop ? (
				<Flex
					style={{
						flexShrink: 0,
						flexDirection: 'column',
						gap: '8px',
						alignItems: 'flex-end',
					}}
				>
					<CartQtyButtonGroup item={item} size='small' />
					<div
						className={`search-dropdown-stock ${
							stock.stock === 0 ? 'search-dropdown-stock-unavailable' : ''
						}`}
						style={{ whiteSpace: 'nowrap' }}
					>
						{stockText}
					</div>
				</Flex>
			) : null}
		</Flex>
	);
};
